import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { HeaderHeightContext } from 'expo-router/react-navigation';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { ChatErrorBoundary } from '@/components/ChatErrorBoundary';
import { ChatLoadError } from '@/components/ChatLoadError';
import { LinkedInquiryBanner } from '@/components/LinkedInquiryBanner';
import { ProductDetailsBar } from '@/components/MedicalOnDemand/ProductDetailsBar';
import { ChatContent } from '@/components/chat/ChatContent';
import {
  ChatHeaderTitle,
  ChatWebContentHeader,
  getChatHeaderItems,
} from '@/components/chat/ChatHeaderContent';
import { ChatScreenContent } from '@/components/chat/ChatScreenContent';
import type { ChatProps } from '@/components/chat/ChatTypes';
import { MobileChatGroupedTabsSection } from '@/components/chat/MobileChatGroupedTabsSection';
import { useComposerFloats } from '@/components/chat/composer/NativeComposerDock';
import { Header } from '@/components/header';
import { useIsHeaderFloating } from '@/components/headerItems/useIsHeaderFloating';
import { SupportToolsPanel } from '@/components/support/SupportToolsPanel';
import { SupportThreadScreen } from '@/components/supportThread/SupportThreadScreen';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import { useAutoOpenReferencesReady } from '@/features/reference-assistant';
import { ChatAnalyticsProvider } from '@/hooks/chat/useChatAnalytics';
import { useGroupedInquiriesEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useInquiryCloseNavigation } from '@/hooks/inbox/useInquiryCloseNavigation';
import { useActAsFromLink } from '@/hooks/inbox/useActAsFromLink';
import { useLinkedSourceConversationId } from '@/hooks/inbox/useLinkedSourceConversationId';
import { useChat } from '@/hooks/useChat';
import { useChatDraft } from '@/hooks/useChatDraft';
import { useCloseConversation } from '@/hooks/useCloseConversation';
import { useInquirySupportAction } from '@/features/support-contact';
import { headerItem } from '@/components/HeaderActions';
import { isReadOnlyConversation } from '@/services/chat/readOnlyInquiryConversation';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useColors } from '@/hooks/useColors';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useModTeamName } from '@/hooks/useModTeamName';
import { useProductInfo } from '@/hooks/useProductInfo';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { useSingleValueSearchParams } from '@/hooks/useSingleValueSearchParams';
import { type InquiryV2 } from '@/services/getInquiriesV2';
import { type GetInquiryV2GroupMember } from '@/services/getInquiryV2';
import { segmentClient } from '@/util/analytics';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getInquirySubject } from '@/util/getInquirySubject';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import {
  getInquiryDisplayParticipant,
  isAnonymousPrimumRoutedInquiry,
  isNoExpertInquiry,
} from '@/util/inquiryDisplay';
import { isTwilioConversationId } from '@/util/isTwilioConversationId';
import { Sentry } from '@/util/sentry';
import { isSameUserId } from '@/utils/isSameUserId';
import { getModRouteLabel, resolveModGroupRoutes } from '@/utils/modHelpers';

/**
 * Chat Container Component
 *
 * @overview
 * Container component that provides responsive header layout and navigation for the chat interface.
 * Handles both web and mobile header rendering with profile navigation.
 *
 * @behavior
 * - Renders responsive header, WebHeader vs Header, based on platform
 * - Provides navigation back to inbox and to recipient profile
 * - Integrates conversation management controls and banners
 * - Handles analytics tracking for profile navigation
 */
function ChatContainer(props: ChatProps) {
  const {
    userId,
    recipient,
    conversation,
    conversationStatus,
    inquiry,
    activeMessageConversationId,
    groupedTabs,
  } = props;

  const { tab } = useLocalSearchParams<{
    id?: string;
    tab?: string;
  }>();

  const { referenceConversationId, action, source, message } = useSingleValueSearchParams([
    'referenceConversationId',
    'action',
    'source',
    'message',
  ] as const);

  const { colors } = useColors();

  const isCompactLayout = useIsCompactLayout();

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const [chatKeyboardOffset, setChatKeyboardOffset] = useState<number | undefined>(undefined);

  const chatShellRef = useRef<React.ComponentRef<typeof HStack>>(null);

  const isHeaderFloating = useIsHeaderFloating();

  const headerHeight = useContext(HeaderHeightContext) ?? 0;

  const [pinnedChromeHeight, setPinnedChromeHeight] = useState(0);

  /**
   * Native only. The message list fills the screen and scrolls under the header, so the bar
   * floats over it with the system's soft edge, as on every other screen. Banners and grouped
   * tabs are pinned beneath the bar rather than stacked above the list: anything above the
   * list in the tree stops iOS finding it, and would sit under the bar anyway. The list pads
   * its first message clear of both.
   *
   * Where the bar is opaque it takes its own room in the layout, so only the pinned chrome is
   * inset. Where pinned chrome shows under a floating bar, the strip behind the bar is painted
   * too, so messages pass under one solid band rather than vanishing under the banner and
   * reappearing in the bar above it.
   */
  const headerInset = isHeaderFloating ? headerHeight : 0;

  const messageListTopInset = isWeb ? 0 : headerInset + pinnedChromeHeight;

  const {
    conversation: activeMessageConversation,
    recipient: activeMessageRecipient,
    conversationStatus: activeMessageConversationStatus,
    inquiry: activeMessageInquiry,
    error: activeMessageConversationError,
    retry: retryActiveMessageConversation,
  } = useChat(activeMessageConversationId ?? conversation.id);

  const lastResolvedActiveContentRef = useRef<{
    conversation?: typeof activeMessageConversation;
    recipient?: typeof activeMessageRecipient;
    conversationStatus?: typeof activeMessageConversationStatus;
    inquiry?: typeof activeMessageInquiry;
  }>({});

  useEffect(() => {
    if (
      !groupedTabs ||
      !activeMessageConversation ||
      !activeMessageRecipient ||
      !activeMessageConversationStatus ||
      !activeMessageInquiry
    ) {
      return;
    }

    lastResolvedActiveContentRef.current = {
      conversation: activeMessageConversation,
      recipient: activeMessageRecipient,
      conversationStatus: activeMessageConversationStatus,
      inquiry: activeMessageInquiry,
    };
  }, [
    groupedTabs,
    activeMessageConversation,
    activeMessageRecipient,
    activeMessageConversationStatus,
    activeMessageInquiry,
  ]);

  const currentConversation = groupedTabs
    ? (activeMessageConversation ??
      lastResolvedActiveContentRef.current.conversation ??
      conversation)
    : conversation;

  /**
   * The fallbacks above keep the previous sibling on screen while a newly
   * selected tab loads, which avoids a flicker but leaves the rendered
   * conversation addressing the wrong thread. Surfacing that mismatch lets the
   * composer stay shut until the selection and the rendered thread agree.
   */
  const isResolvingActiveConversation =
    !!groupedTabs &&
    !!activeMessageConversationId &&
    currentConversation.id !== activeMessageConversationId;

  const currentRecipient = groupedTabs
    ? (activeMessageRecipient ?? lastResolvedActiveContentRef.current.recipient ?? recipient)
    : recipient;

  const currentConversationStatus = groupedTabs
    ? (activeMessageConversationStatus ??
      lastResolvedActiveContentRef.current.conversationStatus ??
      conversationStatus)
    : conversationStatus;

  const currentInquiry = groupedTabs
    ? (activeMessageInquiry ?? lastResolvedActiveContentRef.current.inquiry ?? inquiry)
    : inquiry;

  const { draftMessage, setDraftMessage, clearDraft } = useChatDraft(
    currentConversation.id,
    userId,
    message || undefined
  );

  const hasClearedInitialMessageRef = useRef(false);

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'chat',
      message: 'Conversation selected',
      data: {
        conversationId: currentConversation.id,
        inquiryId: currentInquiry.id,
        recipientId: currentRecipient.id,
        recipientUserType: currentRecipient.userType,
      },
      level: 'info',
    });
  }, [currentConversation.id, currentInquiry.id, currentRecipient.id, currentRecipient.userType]);

  const headerConversation = groupedTabs ? currentConversation : conversation;

  const { bottom } = useSafeAreaInsets();

  /**
   * Where the composer floats as glass, the chat runs to the screen's bottom edge and the
   * messages scroll under the tab bar, as under the header. The composer rides above the bar
   * instead of the screen stopping at it, and anything drawn in its place pads itself clear.
   */
  const scrollsUnderTabBar = useComposerFloats();

  const { productDisplay } = useProductInfo(headerConversation);

  const isAuthorView = isSameUserId(inquiry.userId, userId);

  const currentDisplayParticipant = getInquiryDisplayParticipant(
    currentInquiry,
    currentRecipient,
    isInboxGroupedInquiriesEnabled
  );

  const showAnonymousRoutingChrome = isAnonymousPrimumRoutedInquiry(currentInquiry);

  const isAskAnonymouslyRelayView =
    (showAnonymousRoutingChrome || isAnonymousPrimumRoutedInquiry(inquiry)) && !isAuthorView;

  /**
   * Recipient (MOD expert) view: surface the submitter as the chat-header
   * participant so the avatar/name describe who they're answering. Submitter
   * view keeps the existing other-participant resolution (the MOD expert).
   */
  const otherParticipantForHeader = isAuthorView
    ? currentDisplayParticipant
    : currentInquiry.author;

  const headerParticipant = isAskAnonymouslyRelayView
    ? currentRecipient
    : otherParticipantForHeader;

  const headerRecipient = { ...currentRecipient, ...headerParticipant };

  const { name } = getProfileNameAndInitials(currentDisplayParticipant);

  const { name: headerParticipantName, formalName: headerParticipantFormalName } =
    getProfileNameAndInitials(headerRecipient);

  const groupRouteAssignment = resolveModGroupRoutes(
    (currentInquiry as { groupInquiries?: GetInquiryV2GroupMember[] }).groupInquiries ?? [
      currentInquiry as unknown as Parameters<typeof resolveModGroupRoutes>[0][number],
    ]
  );

  const inquiryRoute = groupRouteAssignment.get(currentInquiry.conversationId) ?? null;

  /**
   * Gate the team-name rebrand on `MOD_DIRECT_TO_MSL`. When the flag is OFF
   * the chat header falls back to the legacy team name so users not in the
   * rollout see the pre-MSL labeling.
   */
  const modTeamName = useModTeamName(productDisplay?.displayName);

  const baseDisplayName = isAuthorView ? (modTeamName ?? name) : headerParticipantName;

  /**
   * On MSL-routed siblings the recipient sees the submitter's postal code
   * inline with their name, alongside a copy-to-clipboard affordance — so
   * the MOD expert can look up the territory on J&J Medical Connect.
   */
  const recipientPostalCode =
    !isAuthorView && inquiryRoute === 'msl' ? currentInquiry.title?.postalCode : undefined;

  /**
   * Submitter view substitutes the route label as the secondary header line
   * (so they see "J&J Medical Information Team" / "J&J MSL"). Recipient
   * view keeps the submitter name on the secondary line; the postal code is
   * rendered separately by the header so it can carry a copy button.
   */
  const displayName =
    isAuthorView && inquiryRoute ? getModRouteLabel(inquiryRoute) : baseDisplayName;

  /**
   * Recipient side: surface the active route as a muted caption under the
   * subject. Renders for both single-sibling cards and multi-sibling groups
   * (where it tracks the currently-selected tab). Submitter side already
   * carries the route via `displayName`.
   */
  const routeCaption = !isAuthorView && inquiryRoute ? getModRouteLabel(inquiryRoute) : undefined;

  const isSupportToolsEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_FULLSCREEN_CONVERSATION);

  useAutoOpenReferencesReady({
    conversationId: currentConversation.id,
    action,
    source,
  });

  const isSupportConversation = currentInquiry.origin === 'primum_support';

  const showMentorHeaderBadge = currentInquiry?.title?.type === 'mentor_inquiry';

  /**
   * Fellows Corner submits every mentor inquiry under one fixed cancer-focus
   * subject, so it never varies inquiry to inquiry and can't help anyone scan
   * their inbox. The participant's name is the one fact that actually
   * differs, so it takes the subject slot the same way a support
   * conversation's does.
   */
  const displaySubject =
    isSupportConversation || showMentorHeaderBadge
      ? headerParticipantName
      : (productDisplay?.displaySubject ??
        getInquirySubject(headerConversation.attributes as InquiryV2['title']));

  const groupedTabsDescription = isAskAnonymouslyRelayView
    ? `Replying to ${headerParticipantName}${displaySubject ? ` · ${displaySubject}` : ''}`
    : undefined;

  const showAnonymousHeaderBadge =
    (showAnonymousRoutingChrome || isAnonymousPrimumRoutedInquiry(inquiry)) &&
    isInboxGroupedInquiriesEnabled;

  const {
    closeHeaderItem,
    closeMenuAction,
    topBanner,
    isAuthor,
    closedConversation,
    webInquiryModals,
  } = useCloseConversation({
    userId,
    conversation: currentConversation,
    recipientId: currentRecipient.id.toString(),
    recipientName: headerParticipantFormalName,
    conversationStatus: currentConversationStatus,
    inquiryFeedbackStatus: currentInquiry.feedbackStatus ?? 'Pending',
    authorId: currentInquiry.userId,
    inquiry: currentInquiry,
    isAskAnonymouslyRelayView,
  });

  const contactSupportAction = useInquirySupportAction({
    inquiry: {
      inquiryId: currentInquiry.id,
      conversationId: currentConversation.id,
      title: displaySubject || headerParticipantFormalName || 'This inquiry',
    },
    isReferenceable:
      isSameUserId(currentInquiry.userId, userId) ||
      isSameUserId(currentInquiry.recipient?.id, userId),
    isHidden: isReadOnlyConversation(currentConversation),
  });

  /**
   * Phones keep Close in the "…" menu beside Contact support. Web keeps Close
   * as a word in the bar, so its menu holds Contact support alone.
   */
  const inquiryMenuItem = headerItem.inquiryActions(
    isWeb ? [contactSupportAction] : [closeMenuAction, contactSupportAction],
    (isWeb || closeMenuAction.isHidden) && contactSupportAction.isHidden
  );

  /** Use feature flag (not groupedTabs) to ensure advancement works for last inquiry in group. */
  useInquiryCloseNavigation({
    currentConversationId: currentConversation.id,
    currentConversationStatus,
    groupedRoutes: groupedTabs?.routes ?? [],
    tabSelected: tab ?? 'open',
    isGroupedEnabled: isInboxGroupedInquiriesEnabled,
  });

  const { navigate, setParams } = useRouter();

  /**
   * Clears the `message` query param once useChatDraft has consumed it as the composer preload,
   * so a refresh doesn't re-preload and overwrite edits. The hook captures the preload
   * synchronously on mount, so clearing the param here can't race it.
   */
  useEffect(() => {
    if (!message || hasClearedInitialMessageRef.current) {
      return;
    }

    hasClearedInitialMessageRef.current = true;

    setParams({ message: undefined });
  }, [message, setParams]);

  const handleLinkedBannerView = useCallback(
    (id: string) => {
      const { name, nameWithoutDoctor } = getProfileNameAndInitials(recipient);

      const subject = getInquirySubject(conversation.attributes as InquiryV2['title']);

      const avatarUrl = getAvatarUrl(recipient.avatar, true) as string | undefined;

      navigate({
        pathname: '/inbox/chat/[id]',
        params: {
          id,
          recipientId: recipient.id.toString(),
          name,
          nameWithoutDoctor,
          subject,
          avatarUrl: avatarUrl || '',
        },
      });
    },
    [recipient, conversation.attributes, navigate]
  );

  const refConversationId: string | undefined =
    (currentInquiry as Partial<InquiryV2> & { referenceInquiryIds?: string[] })
      ?.referenceInquiryIds?.[0] ??
    (referenceConversationId || undefined);

  const linkedSourceConversationId = useLinkedSourceConversationId({
    referenceConversationId: refConversationId,
    recipientId: currentInquiry?.recipient?.id,
  });

  const { isPrimumUser } = useCognitoGroups();

  const showNoExpertHeaderBadge =
    isPrimumUser && !isAuthorView && isNoExpertInquiry(currentInquiry);

  const onViewHeaderProfile = () => {
    if (segmentClient) {
      Promise.resolve(
        segmentClient.track(ANALYTICS_EVENTS.PROFILE_VIEWED, {
          source: 'chat_header',
        })
      ).catch(() => undefined);
    }

    navigate(`/profile/${currentRecipient.id}`);
  };

  const headerProfileProps = {
    conversationId: currentConversation.id,
    recipient: currentRecipient,
    displayName,
    displaySubject: isWeb && isAskAnonymouslyRelayView ? '' : displaySubject,
    onViewProfile: onViewHeaderProfile,
    showAnonymousBadge: showAnonymousHeaderBadge,
    showMentorBadge: showMentorHeaderBadge,
    showNoExpertBadge: showNoExpertHeaderBadge,
    showSupportBadge: isSupportConversation,
    routeCaption,
    postalCode: recipientPostalCode,
  };

  const headerTitle = () => <ChatHeaderTitle {...headerProfileProps} />;

  const webContentHeader = (
    <ChatWebContentHeader
      conversationId={currentConversation.id}
      recipient={currentRecipient}
      displayName={displayName}
      displaySubject={displaySubject}
      onViewProfile={onViewHeaderProfile}
      showAnonymousBadge={showAnonymousHeaderBadge}
      showMentorBadge={showMentorHeaderBadge}
      showNoExpertBadge={showNoExpertHeaderBadge}
      showSupportBadge={isSupportConversation}
      routeCaption={routeCaption}
      postalCode={recipientPostalCode}
    />
  );

  const isSupportToolsPanelVisible = isWeb && !isCompactLayout && isSupportToolsEnabled;

  const groupedTabsSection = (
    <MobileChatGroupedTabsSection
      groupedTabs={
        groupedTabs ? { ...groupedTabs, description: groupedTabsDescription } : groupedTabs
      }
      isSmallDevice={isCompactLayout}
      activeTabTextColor={colors.typography[900]}
      inactiveTabTextColor={colors.gray}
      bannerSlot={
        refConversationId && isPrimumUser && isWeb ? (
          <LinkedInquiryBanner
            conversationId={refConversationId}
            loggedInUserId={userId}
            label="Linked from"
            containerClassName="flex-shrink-0 items-start justify-start border-t border-outline-100 bg-background-0 px-5 py-2 md:px-7"
            onView={(id: string) => handleLinkedBannerView(linkedSourceConversationId ?? id)}
          />
        ) : undefined
      }
    />
  );

  const productDetailsBar = productDisplay?.productInfo ? (
    <ProductDetailsBar product={productDisplay.productInfo} products={productDisplay.products} />
  ) : null;

  const nativeLinkedInquiryBanner =
    refConversationId && isPrimumUser && !isWeb ? (
      <Box
        collapsable={false}
        className="min-h-12 w-full flex-shrink-0 border-t border-outline-100 bg-background-0"
        style={{ minHeight: 48 }}>
        <LinkedInquiryBanner
          conversationId={refConversationId}
          loggedInUserId={userId}
          label="Linked from"
          compact
          containerClassName="w-full self-stretch items-stretch justify-start px-5 py-2"
          onView={(id: string) => handleLinkedBannerView(linkedSourceConversationId ?? id)}
        />
      </Box>
    ) : null;

  const chatShell = (
    <HStack
      ref={chatShellRef}
      className="flex-1"
      {...(!isWeb && {
        onLayout: () => {
          chatShellRef.current?.measureInWindow?.((_x, y) => {
            if (y > 0) {
              setChatKeyboardOffset(y);
            }
          });
        },
      })}>
      <VStack className="flex-1">
        {activeMessageConversationError ? (
          // Without this the composer would sit disabled forever: a sibling
          // that never resolves leaves the rendered conversation stuck on the
          // previous tab, so the resolving guard can never clear on its own.
          <VStack
            className="flex-1"
            style={{
              paddingTop: messageListTopInset,
              paddingBottom: scrollsUnderTabBar ? bottom : 0,
            }}>
            <ChatLoadError
              errorType={activeMessageConversationError.type}
              onRetry={retryActiveMessageConversation}
            />
          </VStack>
        ) : (
          <ChatAnalyticsProvider
            inquiryId={currentInquiry.id}
            conversationId={currentConversation.id}
            recipientId={currentRecipient.id}
            recipientName={getProfileNameAndInitials(currentRecipient).name}
            recipientUserType={currentRecipient.userType}>
            <ChatContent
              conversationStatus={currentConversationStatus}
              userId={userId}
              conversation={currentConversation}
              recipient={currentRecipient}
              inquiry={currentInquiry}
              isAuthor={isAuthor}
              closedConversation={closedConversation}
              draftMessage={draftMessage}
              setDraftMessage={setDraftMessage}
              clearDraft={clearDraft}
              keyboardVerticalOffset={chatKeyboardOffset}
              topInset={messageListTopInset}
              bottomInset={scrollsUnderTabBar ? bottom : 0}
              isResolvingActiveConversation={isResolvingActiveConversation}
            />
          </ChatAnalyticsProvider>
        )}
      </VStack>

      {isSupportToolsPanelVisible && (
        <SupportToolsPanel recipient={recipient} onAppendToChat={(text) => setDraftMessage(text)} />
      )}
    </HStack>
  );

  const pageTitle = displaySubject || headerParticipantFormalName || 'Inquiry';

  const header = (
    <Header
      isBackButton
      backRoute={ROUTES.INBOX}
      documentTitle={pageTitle}
      contentScrollsUnderHeader
      {...(isWeb ? { headerTitle } : { leftItems: getChatHeaderItems(headerProfileProps) })}
      rightItems={isWeb ? [closeHeaderItem, inquiryMenuItem] : [inquiryMenuItem]}
    />
  );

  const containerClassName = twJoin(
    'mx-auto w-full flex-1 web:max-w-screen-2xl md:px-10',
    !scrollsUnderTabBar && 'mb-1'
  );

  const containerStyle = {
    paddingBottom: scrollsUnderTabBar ? 0 : bottom + (Platform.OS === 'android' ? 20 : 0),
  };

  if (isWeb) {
    return (
      <VStack className={containerClassName} style={containerStyle}>
        {header}
        {!isCompactLayout ? webContentHeader : null}
        {topBanner}
        {webInquiryModals}
        {productDetailsBar}
        {groupedTabsSection}
        {chatShell}
      </VStack>
    );
  }

  return (
    <VStack className={containerClassName} style={containerStyle}>
      {header}

      <View className="flex-1">
        {chatShell}

        <View
          pointerEvents="box-none"
          className="absolute left-0 right-0"
          style={{ top: headerInset }}
          onLayout={({ nativeEvent }) => {
            const measured = Math.round(nativeEvent.layout.height);

            setPinnedChromeHeight((current) => (current === measured ? current : measured));
          }}>
          {topBanner}
          {productDetailsBar}
          {groupedTabsSection}
          {nativeLinkedInquiryBanner}
        </View>

        {headerInset > 0 && pinnedChromeHeight > 0 ? (
          <View
            pointerEvents="none"
            className="absolute left-0 right-0 top-0 bg-background-0"
            style={{ height: headerInset }}
          />
        ) : null}
      </View>
    </VStack>
  );
}

/**
 * One route for both kinds of conversation, because notification and deep
 * links name only the id. A Twilio id is an inquiry and opens the inquiry
 * chat; anything else is a native support thread, which is decided before any
 * Twilio code runs so the thread opens even when no Twilio client exists.
 *
 * A staff text for a shared account names the account in `actAs`, and the
 * conversation belongs to that account, so nothing loads until the switch to it
 * has landed. Loading first would fetch the chat as the staff member, who is
 * not part of it.
 */
export default function ChatScreen() {
  const { id, actAs } = useLocalSearchParams<{ id: string; actAs?: string }>();

  const { isSwitching } = useActAsFromLink(actAs);

  if (isSwitching) {
    return <View className="flex-1 bg-background-0" />;
  }

  return (
    <ChatErrorBoundary conversationId={id}>
      {id && !isTwilioConversationId(id) ? (
        <SupportThreadScreen conversationId={id} />
      ) : (
        <ChatScreenContent ChatContainerComponent={ChatContainer} />
      )}
    </ChatErrorBoundary>
  );
}
